import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, ArrowUpRight } from "lucide-react";
import { api, errText, useAuth } from "@/lib/auth";
import { FULL_PRICE, PARTNER_PRICE, UPGRADE_PRICE, hasPartnerPrice, isUpgrade, usePartnerOffer } from "@/lib/partnerInvite";

const field = "w-full bg-transparent border-b border-[#D9B47A]/30 py-4 text-lg placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors";

// Price card + account fields + Stripe checkout for one product ("gates" or "quantum").
export default function CheckoutCard({ product, already, price = "$297", note = "One payment. Yours forever." }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const offer = usePartnerOffer();
  const upgrade = isUpgrade(user, product);
  const partner = !upgrade && hasPartnerPrice(user, offer);

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/initiates/checkout", { ...(user ? {} : form), origin_url: window.location.origin, product, partner_code: offer?.code ?? "" });
      if (data?.already) window.location.assign(already);
      else if (data?.checkout_url) window.location.assign(data.checkout_url);
      else setError("Checkout could not start. Please try again.");
    } catch (err) {
      setError(errText(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="lg:sticky lg:top-32 border border-[#D9B47A]/25 bg-[#0D0908] p-8 sm:p-10">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#A8998C]">Lifetime access</p>
      {upgrade && <p data-testid="initiate-upgrade-badge" className="mt-4 inline-flex rounded-full border border-[#D9B47A]/50 px-3 py-1.5 text-[12px] text-[#D9B47A]">Your upgrade price · you already own the other app</p>}
      {partner && <p data-testid="initiate-partner-badge" className="mt-4 inline-flex rounded-full border border-[#E8838E]/50 px-3 py-1.5 text-[12px] text-[#E8838E]">Partner invitation{offer?.inviter && !user?.partner_discount ? ` from ${offer.inviter}` : ""} · 25% off</p>}
      <p data-testid="initiate-price" className="font-display text-6xl mt-4">{upgrade ? UPGRADE_PRICE : partner ? PARTNER_PRICE[product] : price} <span className="text-2xl text-[#D9B47A]">AUD</span></p>
      {(partner || upgrade) && <p data-testid="initiate-full-price" className="mt-1 text-[15px] text-[#A8998C] line-through">{FULL_PRICE[product]} AUD</p>}
      <p className="text-sm text-[#A8998C] mt-2">{note}</p>
      <form onSubmit={pay} data-testid="initiate-form" className="mt-8">
        {user ? (
          <p className="text-[#CFC2B6]">Signed in as <span className="text-[#F2E8DF]">{user.email}</span></p>
        ) : (
          <>
            <input data-testid="initiate-name" required maxLength={120} value={form.name} onChange={set("name")} placeholder="Your name" className={field} />
            <input data-testid="initiate-email" required type="email" value={form.email} onChange={set("email")} placeholder="Your email" className={field} />
            <input data-testid="initiate-password" required type="password" minLength={8} value={form.password} onChange={set("password")} placeholder="Create a password (8+ characters)" className={field} />
          </>
        )}
        {error && <p data-testid="initiate-error" className="mt-4 text-sm text-[#E8838E]">{error}</p>}
        <button data-testid="initiate-pay" disabled={busy} className="group mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#C2414F] text-[#F2E8DF] py-5 font-tt-mono text-[12px] uppercase tracking-[0.25em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Continue to secure payment
          <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
        </button>
        <Link to="/gift" data-testid="initiate-gift-link" className="mt-4 block text-center text-[13px] text-[#A8998C] underline underline-offset-4 hover:text-[#D9B47A]">Or give it as a gift</Link>
      </form>
      <p className="mt-5 text-[13px] text-[#A8998C]">Payments are processed securely by Stripe. Your account is created now so everything opens the moment payment completes. One account works for both apps.</p>
      {!user && <Link to="/signin" data-testid="initiate-signin" className="block mt-4 text-sm text-[#CFC2B6] underline hover:text-[#D9B47A]">Already have an account? Sign in</Link>}
    </div>
  );
}
